"use client";

import { useState, useEffect, useCallback } from "react";
import { FiX, FiLoader } from "react-icons/fi";
import { useVisibilityRef } from "@/hooks/useVisibilityRef";
import { ChevronDownIcon } from "@/app/components/icons/ChevronDownIcon";
import { ListChevronsUpDownIcon } from "@/app/components/icons/ListChevronsUpDownIcon";
import { useAuth } from "@/hooks/useAuth";
import { authFetch } from "@/lib/authFetch";
import { useOrdersScoring } from "@/hooks/usePolymarketRewards";
import { useT } from "@/hooks/useT";
import { formatCents } from '@/lib/format-cents';

interface OpenOrder {
  id: string;
  side: "BUY" | "SELL";
  outcomeIndex: number;
  price: number;
  size: number;
  sizeFilled: number;
  total: number;
  asset_id?: string;
}

interface OpenOrdersProps {
  marketSlug: string;
  conditionId?: string | null;
  outcomes: string[];
  /** Event-scope filter. When provided, the component fetches ALL of the
   * user's open orders and keeps only those whose `asset_id` (CLOB
   * tokenId) matches one of the event's sub-markets. Avoids
   * flicker-on-switch when the user toggles sub-markets and lets the
   * sidebar show orders across the full event. */
  allowedTokenIds?: string[];
  /** Event-scope metadata keyed by tokenId → sub-market info. Used to
   * render the sub-market label + YES/NO outcome per row so the user
   * can tell which market an order belongs to without inspecting the
   * asset_id. Each row picks the outcome label from `outcomes[0/1]`
   * based on the row's outcomeIndex. */
  marketMetaByTokenId?: Record<string, { image?: string | null; label: string; outcomes: string[] }>;
}

export default function OpenOrders({ marketSlug, conditionId, outcomes, allowedTokenIds, marketMetaByTokenId }: OpenOrdersProps) {
  const t = useT();
  const eventScope = Array.isArray(allowedTokenIds) && allowedTokenIds.length > 0;
  const allowedTokenSet = new Set((allowedTokenIds || []).map((tid) => String(tid).toLowerCase()));
  const { isLoading, isReady, isLoggedIn } = useAuth();

  const [orders, setOrders] = useState<OpenOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [cancellingAll, setCancellingAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [headerHovered, setHeaderHovered] = useState(false);
  const visRef = useVisibilityRef();

  // Live scoring status for each resting order. Polled every 60s via backend
  // (CLOB L2-auth). Enables the "⚡ Earning" badge so the user sees which
  // of their orders are inside the reward band vs not.
  const orderIds = orders.map((o) => o.id);
  const scoringQ = useOrdersScoring(orderIds, isLoggedIn && orderIds.length > 0);
  const scoringMap = scoringQ.data?.scoring || {};

  const fetchOrders = useCallback(async () => {
    if (isLoading || !isLoggedIn) {
      setOrders([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Event scope: no server-side filter — fetch all user orders, then
      // keep only those whose market is one of the event's sub-markets.
      const url = eventScope
        ? `/api/trade/orders`
        : `/api/trade/orders?marketSlug=${marketSlug || conditionId}`;
      const response = await authFetch(url);
      if (response.ok) {
        const data = await response.json();
        const all = (data.orders || []) as Array<OpenOrder & { asset_id?: string }>;
        if (eventScope) {
          const kept = all.filter((o) => {
            const tid = String((o as any).asset_id || (o as any).token_id || '').toLowerCase();
            return tid && allowedTokenSet.has(tid);
          });
          setOrders(kept);
        } else {
          setOrders(all);
        }
      }
    } catch (err: any) {
    } finally {
      setLoading(false);
    }
  }, [isReady, isLoggedIn, marketSlug, conditionId, eventScope]);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(() => { if (visRef.current) fetchOrders(); }, 15000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const handleCancelOrder = async (orderId: string) => {
    setCancelling(orderId);
    try {
      const response = await authFetch("/api/trade/cancel", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderId }),
      });

      if (response.ok) {
        setOrders(prev => prev.filter(o => o.id !== orderId));
      } else {
        const data = await response.json();
        setError(data.error || "Failed to cancel order");
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCancelling(null);
    }
  };

  const handleCancelAll = async () => {
    if (orders.length === 0) return;

    setCancellingAll(true);
    try {
      const response = await authFetch("/api/trade/cancel-all", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ marketSlug }),
      });

      if (response.ok) {
        setOrders([]);
      } else {
        const data = await response.json();
        setError(data.error || "Failed to cancel orders");
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCancellingAll(false);
    }
  };

  if (!isLoggedIn) {
    return null;
  }

  const outcome1Label = outcomes[0]?.toUpperCase() || "YES";
  const outcome2Label = outcomes[1]?.toUpperCase() || "NO";

  return (
    <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        onMouseEnter={() => setHeaderHovered(true)}
        onMouseLeave={() => setHeaderHovered(false)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-[#0f0f12]/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <ListChevronsUpDownIcon size={20} className="text-gray-400 dark:text-gray-500" isHovered={headerHovered} />
          <h3 className="font-monument text-lg text-gray-900 dark:text-white">{t("openOrders.title")}</h3>
          {orders.length > 0 && (
            <span className="px-2 py-0.5 bg-gray-100 dark:bg-[#0f0f12] text-gray-900 dark:text-white text-xs font-inter-tight font-semibold rounded-[5px]">
              {orders.length}
            </span>
          )}
        </div>
        <div className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
          <ChevronDownIcon size={20} className="text-gray-400" isHovered={headerHovered} />
        </div>
      </button>

      <div className={`transition-[max-height,opacity] duration-200 ease-in-out ${isExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
        <div className="px-4 pb-4">
          {loading && orders.length === 0 && (
            <div className="animate-pulse space-y-2">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="h-16 bg-gray-100 dark:bg-[#0f0f12] rounded-[5px]" />
              ))}
            </div>
          )}

          {!loading && orders.length === 0 && (
            <div className="text-center py-4">
              <p className="font-satoshi text-sm text-gray-500 dark:text-gray-400">{t("openOrders.noOpen")}</p>
            </div>
          )}

          {orders.length > 0 && (
            <>
              <div className="flex justify-end mb-3">
                <button
                  onClick={handleCancelAll}
                  disabled={cancellingAll}
                  className="font-inter-tight text-xs font-semibold text-[#FF1744] hover:text-white hover:bg-[#FF1744] px-3 py-1.5 rounded-[5px] border border-[#FF1744]/30 hover:border-[#FF1744] transition-all disabled:opacity-50"
                >
                  {cancellingAll ? (
                    <span className="flex items-center gap-1">
                      <FiLoader className="w-3 h-3 animate-spin" />
                      {t("openOrders.cancelling")}
                    </span>
                  ) : (
                    t("openOrders.cancelAll")
                  )}
                </button>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-[#FF1744]/10 border border-[#FF1744]/30 rounded-[5px]">
                  <p className="font-satoshi text-xs text-[#FF1744]">{error}</p>
                </div>
              )}

              {/* Grid columns — an extra "Market" column when event-scope
                  so the user can tell which sub-market each order belongs
                  to. Cancel button still lives in the last cell. */}
              <div className={`grid ${eventScope ? 'grid-cols-[1fr_60px_50px_60px_70px_70px_28px]' : 'grid-cols-6'} gap-2 mb-2 px-3 text-xs font-inter-tight font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide`}>
                {eventScope && <div>{t("openOrders.col.market")}</div>}
                <div>{t("openOrders.col.side")}</div>
                <div>{t("openOrders.col.outcome")}</div>
                <div className="text-right">{t("openOrders.col.price")}</div>
                <div className="text-right">{t("openOrders.col.filled")}</div>
                <div className="text-right">{t("openOrders.col.total")}</div>
                <div className="text-right"></div>
              </div>

              <div className="space-y-2">
                {orders.map((order) => {
                  const isYes = order.outcomeIndex === 0;
                  const isBuy = order.side === "BUY";
                  const tid = String((order as any).asset_id || (order as any).token_id || '').toLowerCase();
                  const meta = eventScope ? marketMetaByTokenId?.[tid] : undefined;
                  // Pick outcome label from the row's own market when in event
                  // scope (the sidebar's `outcomes` prop only describes the
                  // currently-selected sub-market, not this order's market).
                  const rowOutcomes = meta?.outcomes || outcomes;
                  const rowOutcome1Label = rowOutcomes?.[0]?.toUpperCase() || "YES";
                  const rowOutcome2Label = rowOutcomes?.[1]?.toUpperCase() || "NO";
                  const outcomeLabel = isYes ? rowOutcome1Label : rowOutcome2Label;

                  return (
                    <div
                      key={order.id}
                      className={`grid ${eventScope ? 'grid-cols-[1fr_60px_50px_60px_70px_70px_28px]' : 'grid-cols-6'} gap-2 items-center p-3 rounded-[5px] bg-gray-50 dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A] hover:border-[#25252B] transition-colors`}
                    >
                      {eventScope && (
                        <div className="flex items-center gap-1.5 min-w-0">
                          {meta?.image && (
                            <div className="relative w-5 h-5 rounded overflow-hidden bg-gray-200 dark:bg-[#18181D] flex-shrink-0">
                              {/* eslint-disable-next-line @next/next/no-img-element */}
                              <img src={meta.image} alt="" className="w-full h-full object-cover" />
                            </div>
                          )}
                          <span className="font-inter-tight text-[11px] text-gray-900 dark:text-white truncate">
                            {meta?.label || '—'}
                          </span>
                        </div>
                      )}
                      <div>
                        <span className={`font-inter-tight text-xs font-semibold px-2 py-1 rounded-[5px] ${
                          isBuy
                            ? "bg-[#00FF9D]/10 text-[#00FF9D]"
                            : "bg-[#FF1744]/10 text-[#FF1744]"
                        }`}>
                          {order.side}
                        </span>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <span className={`font-inter-tight text-xs font-semibold ${
                          isYes ? "text-[#00FF9D]" : "text-[#FF1744]"
                        }`}>
                          {outcomeLabel}
                        </span>
                        {scoringMap[order.id] && (
                          <span
                            className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 leading-none"
                            title={t("openOrders.earningRewards")}
                          >
                            ⚡ EARN
                          </span>
                        )}
                      </div>

                      <div className="text-right">
                        <span className="font-monument text-sm text-gray-900 dark:text-white">
                          {formatCents((order.price ?? 0), 2)}
                        </span>
                      </div>

                      <div className="text-right">
                        <span className="font-satoshi text-xs text-gray-600 dark:text-gray-300">
                          {(order.sizeFilled ?? 0).toFixed(0)} / {(order.size ?? 0).toFixed(0)}
                        </span>
                      </div>

                      <div className="text-right">
                        <span className="font-inter-tight text-sm font-medium text-gray-900 dark:text-white">
                          ${(order.total ?? 0).toFixed(2)}
                        </span>
                      </div>

                      <div className="text-right">
                        <button
                          onClick={() => handleCancelOrder(order.id)}
                          disabled={cancelling === order.id}
                          className="p-1.5 rounded-[5px] text-[#FF1744] bg-[#FF1744]/10 hover:bg-[#FF1744] hover:text-white transition-all disabled:opacity-50"
                          title={t("openOrders.cancelOrder")}
                        >
                          {cancelling === order.id ? (
                            <FiLoader className="w-4 h-4 animate-spin" />
                          ) : (
                            <FiX className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-3 text-center">
                <p className="font-satoshi text-xs text-gray-400 dark:text-gray-500">
                  {t("openOrders.validUntilCancelled")}
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
